import React from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { ExternalLink } from "lucide-react";
import { requireCoachAuth } from "~/lib/coach-middleware";
import { getResourcesByCoachId } from "~/lib/server/coach";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";

export const getCoachResources = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const coach = context.coach;
    const resources = await getResourcesByCoachId(coach.id);
    return resources;
  });

export const Route = createFileRoute("/coach/_backend/edit-profile/resources/")(
  {
    head: () => ({
      meta: [
        {
          title: "Resources – Dream Coach Match",
        },
      ],
    }),
    loader: async ({ context }) => {
      // Load resources for the coach
      await context.queryClient.ensureQueryData({
        queryKey: ["coach", "resources"],
        queryFn: () => getCoachResources(),
      });

      return {};
    },
    component: ResourcesPage,
  },
);

function ResourcesPage() {
  const coach = useCoachPrivateProfileRequired();

  const {
    data: resources = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["coach", "resources"],
    queryFn: () => getCoachResources(),
  });

  if (isLoading) {
    return <div>Loading resources...</div>;
  }

  if (error) {
    return <div>Error loading resources: {error.message}</div>;
  }

  return (
    <>
      <div className="flex h-16 items-center justify-between gap-4">
        <h1 className="font-heading text-text-primary grow text-2xl">
          Resources
        </h1>
        <Link
          to="/$username"
          params={{ username: coach.username }}
          target="_blank"
        >
          <Button
            variant="outline"
            type="button"
            className="flex items-center gap-2"
          >
            View Profile <ExternalLink />
          </Button>
        </Link>
        <Link
          to="/coach/edit-profile/resources/$resourceId"
          params={{ resourceId: "new" }}
        >
          <Button>Add New Resource</Button>
        </Link>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4">
        {0 < resources.length ? (
          resources.map((resource) => (
            <Card key={resource.id}>
              <CardContent className="flex items-start justify-between">
                <div className="flex-1 space-y-1">
                  <h3 className="font-semibold text-lg">{resource.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {resource.description}
                  </p>
                </div>
                <Link
                  to="/coach/edit-profile/resources/$resourceId"
                  params={{ resourceId: resource.id }}
                  className="ml-4"
                >
                  <Button size="sm">Edit</Button>
                </Link>
              </CardContent>
            </Card>
          ))
        ) : (
          <p className="text-muted-foreground">No resources available.</p>
        )}
      </div>
    </>
  );
}
